import type { Category } from '@shopgnd/shared';
import { getAuthedDataClient, getCatalogClient } from '@/lib/amplify-client';
import { notifyCatalogRefresh } from '@/lib/catalog-refresh';

function mapCategory(row: Record<string, unknown>): Category {
  const homeFeaturedRank =
    row.homeFeaturedRank != null ? Number(row.homeFeaturedRank) : undefined;
  const shopNavRank = row.shopNavRank != null ? Number(row.shopNavRank) : undefined;
  const sortOrder = row.sortOrder != null ? Number(row.sortOrder) : undefined;
  return {
    id: String(row.id),
    name: String(row.name),
    slug: String(row.slug),
    parentId: row.parentId ? String(row.parentId) : undefined,
    imageUrl: row.imageUrl ? String(row.imageUrl) : undefined,
    homeFeaturedRank:
      homeFeaturedRank != null && !Number.isNaN(homeFeaturedRank) ? homeFeaturedRank : undefined,
    shopNavRank:
      shopNavRank != null && !Number.isNaN(shopNavRank) ? shopNavRank : undefined,
    sortOrder: sortOrder != null && !Number.isNaN(sortOrder) ? sortOrder : undefined,
  };
}

function slugifyCategory(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'category';
}

export async function listCategories(): Promise<Category[]> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Category.list();
    return ((res.data ?? []) as Record<string, unknown>[]).map(mapCategory);
  } catch (e) {
    console.error('[ShopGND] listCategories failed', e);
    return [];
  }
}

export async function createCategory(input: {
  name: string;
  slug?: string;
  parentId?: string;
  imageUrl?: string;
  homeFeaturedRank?: number;
  shopNavRank?: number;
  sortOrder?: number;
}): Promise<Category> {
  const name = input.name.trim();
  if (!name) throw new Error('Category name is required');
  const slug = input.slug?.trim() || slugifyCategory(name);

  const client = await getAuthedDataClient();
  const res = await client.models.Category.create({
    name,
    slug,
    parentId: input.parentId,
    imageUrl: input.imageUrl,
    homeFeaturedRank: input.homeFeaturedRank,
    shopNavRank: input.shopNavRank,
    sortOrder: input.sortOrder,
  });
  if (!res.data) throw new Error('Could not create category');
  notifyCatalogRefresh();
  return mapCategory(res.data as Record<string, unknown>);
}

export async function updateCategory(
  id: string,
  patch: Partial<
    Pick<
      Category,
      'name' | 'slug' | 'parentId' | 'imageUrl' | 'homeFeaturedRank' | 'shopNavRank' | 'sortOrder'
    >
  >,
): Promise<Category | null> {
  try {
    const client = await getAuthedDataClient();
    const payload: Record<string, unknown> = { id };
    if (patch.name !== undefined) payload.name = patch.name;
    if (patch.slug !== undefined) payload.slug = patch.slug;
    if (patch.parentId !== undefined) payload.parentId = patch.parentId;
    if (patch.imageUrl !== undefined) payload.imageUrl = patch.imageUrl || null;
    if (patch.homeFeaturedRank !== undefined) {
      payload.homeFeaturedRank = patch.homeFeaturedRank ?? null;
    }
    if (patch.shopNavRank !== undefined) {
      payload.shopNavRank = patch.shopNavRank ?? null;
    }
    if (patch.sortOrder !== undefined) {
      payload.sortOrder = patch.sortOrder ?? null;
    }
    const res = await client.models.Category.update(payload);
    if (!res.data) return null;
    notifyCatalogRefresh();
    return mapCategory(res.data as Record<string, unknown>);
  } catch (e) {
    console.error('[ShopGND] updateCategory failed', e);
    return null;
  }
}

export async function deleteCategory(id: string): Promise<boolean> {
  try {
    const client = await getAuthedDataClient();
    await client.models.Category.delete({ id });
    notifyCatalogRefresh();
    return true;
  } catch {
    return false;
  }
}

/** Clear homeFeaturedRank on a category. */
export async function clearCategoryHomeFeaturedRank(id: string): Promise<void> {
  await updateCategory(id, { homeFeaturedRank: undefined });
}

export async function getCategoryById(id: string): Promise<Category | null> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.Category.get({ id });
    return res.data ? mapCategory(res.data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
